import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../../hooks/use-toast";
import { temTela } from "../../../utils/config";
import { notaPromissoriaService } from "../services/nota-promissoria-service";
import { AtualizarFlagsInput, CriarNpInput, StatusFiltro } from "../types";
import {
  deveRepetirDownload,
  dispararDownload,
  esperaDownload,
  esperarDocumentos,
  TENTATIVAS_COMPRA,
} from "../utils/download";
import { cnabBaixaPronto, documentosProntos, resumoAlerta } from "../utils/vencimento";

export const SCREEN_NP = "gestorNotaPromissoria";

export const npKeys = {
  all: ["nota-promissoria"] as const,
  list: (status: StatusFiltro, ids?: number[]) =>
    [...npKeys.all, "list", status, ids ?? null] as const,
  cadastros: () => [...npKeys.all, "cadastros"] as const,
};

const mensagemErro = (error: any) =>
  error?.response?.data?.message || error?.message || "Erro inesperado.";

export function useNotasPromissorias(
  status: StatusFiltro,
  opts: { ids?: number[]; refetchInterval?: number | false; enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: npKeys.list(status, opts.ids),
    queryFn: async () => {
      const response = await notaPromissoriaService.listar(status, opts.ids);
      return response.data ?? [];
    },
    enabled: opts.enabled ?? true,
    refetchInterval: opts.refetchInterval ?? false,
    staleTime: 1000 * 30,
  });
}

export function useCadastrosNp() {
  return useQuery({
    queryKey: npKeys.cadastros(),
    queryFn: async () => (await notaPromissoriaService.cadastros()).data,
    staleTime: 1000 * 60 * 60,
  });
}

function useInvalidarNp() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: npKeys.all });
}

export function useCriarNp() {
  const { toast } = useToast();
  const invalidar = useInvalidarNp();
  return useMutation({
    mutationFn: async (input: CriarNpInput) => (await notaPromissoriaService.criar(input)).data,
    onSuccess: (data) => {
      toast({
        title: "NP cadastrada",
        description: `Contratos ${data.idContratos.join(", ")}. Gerando CNAB, termo de cessão e NP...`,
      });
      invalidar();
    },
    onError: (error: any) =>
      toast({ title: "Erro ao criar NP", description: mensagemErro(error), variant: "destructive" }),
  });
}

export function useAtualizarFlagsNp() {
  const { toast } = useToast();
  const invalidar = useInvalidarNp();
  return useMutation({
    mutationFn: async (input: AtualizarFlagsInput) =>
      (await notaPromissoriaService.atualizarFlags(input)).data,
    onSuccess: (np) => {
      toast({ title: `NP ${np.id_contrato} atualizada` });
      invalidar();
    },
    onError: (error: any) =>
      toast({ title: "Erro ao atualizar NP", description: mensagemErro(error), variant: "destructive" }),
  });
}

export function useGerarBaixaNp() {
  const { toast } = useToast();
  const invalidar = useInvalidarNp();
  return useMutation({
    mutationFn: async (ids: number[]) => (await notaPromissoriaService.baixar(ids)).data,
    // onSuccess assíncrono de propósito: a mutation segue `isPending` durante a
    // espera, então o botão fica desabilitado enquanto o arquivo não sai.
    onSuccess: async (_data, ids) => {
      toast({
        title: "Baixa iniciada",
        description: "Gerando o CNAB de baixa — a tela avisa quando estiver pronto.",
      });

      // Consulta só os ids pedidos, não a lista inteira.
      const pronto = await esperarDocumentos(
        async () => (await notaPromissoriaService.listar("todas", ids)).data ?? [],
        ids.length,
        cnabBaixaPronto
      );
      invalidar();

      toast(
        pronto
          ? {
              title: "CNAB de baixa pronto",
              description: 'Use "Baixar documentos" para pegar o ZIP — nada é enviado por e-mail.',
            }
          : {
              title: "CNAB de baixa ainda em geração",
              description:
                "Demorou mais que o esperado. Atualize a lista em instantes e confira o ícone de CNAB baixa.",
              variant: "destructive",
            }
      );
    },
    onError: (error: any) =>
      toast({ title: "Erro ao gerar baixa", description: mensagemErro(error), variant: "destructive" }),
  });
}

/**
 * Refaz os documentos de compra de NPs já criadas: é o retry de quando a imagem
 * R falha e a linha fica sem as chaves S3, sem download possível e sem nenhum
 * caminho pela tela a não ser recriar a NP.
 *
 * Espera mais que a baixa porque aqui saem três documentos — o CNAB de remessa
 * e as duas chamadas ao bookbinder.
 */
export function useRegerarCompraNp() {
  const { toast } = useToast();
  const invalidar = useInvalidarNp();
  return useMutation({
    mutationFn: async (ids: number[]) => (await notaPromissoriaService.regerarCompra(ids)).data,
    onSuccess: async (_data, ids) => {
      toast({
        title: "Regeração iniciada",
        description: "Refazendo CNAB de remessa, termo de cessão e cártula.",
      });

      const pronto = await esperarDocumentos(
        async () => (await notaPromissoriaService.listar("todas", ids)).data ?? [],
        ids.length,
        documentosProntos,
        { tentativas: TENTATIVAS_COMPRA }
      );
      invalidar();

      toast(
        pronto
          ? {
              title: "Documentos de compra prontos",
              description: 'Baixe o ZIP em "Baixar documentos".',
            }
          : {
              title: "Documentos ainda não ficaram prontos",
              description:
                "A geração passou do tempo esperado. Confira os ícones de documentos e, se seguirem vermelhos, veja os logs da imagem R.",
              variant: "destructive",
            }
      );
    },
    onError: (error: any) => {
      const semCadastro: string[] | undefined = error?.response?.data?.semCadastro;
      toast({
        title: "Erro ao regerar os documentos",
        description: semCadastro?.length
          ? `Cedente ou sacado fora do cadastro atual em: ${semCadastro.join(", ")}.`
          : mensagemErro(error),
        variant: "destructive",
      });
    },
  });
}

const statusDe = (error: any): number | undefined => error?.response?.status;

/**
 * Baixa o ZIP dos documentos da compra: termo de cessão, NP e CNAB de remessa.
 * O CNAB de baixa tem download próprio (`useDownloadCnabBaixa`).
 *
 * Como nada é enviado por e-mail, este download é o único caminho para os
 * arquivos — por isso as falhas transitórias são repetidas automaticamente,
 * com espera crescente, antes de desistir e pedir uma nova tentativa.
 */
export function useDownloadNp() {
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (ids: number[]) => (await notaPromissoriaService.download(ids)).data,
    retry: deveRepetirDownload,
    retryDelay: esperaDownload,
    onSuccess: ({ url, fileName }) => {
      dispararDownload(url, fileName);
      toast({ title: "Download iniciado", description: fileName });
    },
    onError: (error: any) => {
      const pendentes: string[] | undefined = error?.response?.data?.pendentes;
      if (statusDe(error) === 409) {
        toast({
          title: "Documentos ainda não disponíveis",
          description: pendentes?.length
            ? `Ainda em geração: ${pendentes.join(", ")}. Tente novamente em instantes.`
            : "Os arquivos ainda estão sendo gerados. Tente novamente em instantes.",
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Falha ao baixar os documentos",
        description: `${mensagemErro(error)} Os arquivos continuam no S3 — tente novamente.`,
        variant: "destructive",
      });
    },
  });
}

/**
 * Baixa só o CNAB de baixa — o arquivo que segue sozinho para a Azumi. Uma NP
 * vem como .TXT direto; várias, num ZIP só com os .TXT. Não depende dos
 * documentos da compra, então uma NP com termo pendente ainda baixa o CNAB.
 */
export function useDownloadCnabBaixa() {
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (ids: number[]) => (await notaPromissoriaService.downloadBaixa(ids)).data,
    retry: deveRepetirDownload,
    retryDelay: esperaDownload,
    onSuccess: ({ url, fileName }) => {
      dispararDownload(url, fileName);
      toast({ title: "CNAB de baixa baixado", description: fileName });
    },
    onError: (error: any) => {
      const pendentes: string[] | undefined = error?.response?.data?.pendentes;
      if (statusDe(error) === 409) {
        toast({
          title: "CNAB de baixa ainda não disponível",
          description: pendentes?.length
            ? `Sem CNAB de baixa: ${pendentes.join(", ")}. Gere a baixa primeiro.`
            : "Gere o CNAB de baixa antes de baixá-lo.",
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Falha ao baixar o CNAB de baixa",
        description: `${mensagemErro(error)} O arquivo continua no S3 — tente novamente.`,
        variant: "destructive",
      });
    },
  });
}

/**
 * Alerta global: NPs vencidas sem baixa ou vencendo em até 15 dias.
 * Só consulta quando a tela está habilitada no build (REACT_APP_SCREENS).
 */
export function useAlertaNp() {
  const habilitado = temTela(SCREEN_NP);
  const { data = [], isLoading } = useNotasPromissorias("ativas", {
    enabled: habilitado,
    refetchInterval: habilitado ? 1000 * 60 * 5 : false,
  });
  return { ...resumoAlerta(data), isLoading, habilitado };
}
